import Backend from 'App/Dataman-Backend';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '@/app/api/core/http';
import { createImage, uploadImages } from '@/app/api/core/images';
import { CAT_NEWS, CAT_COMPANY_NEWS, CAT_AI_NEWS } from 'App/utils/Collections';
import { NEWS_PATHS } from './paths';

// news-app is an external service: a failed response is returned as-is instead of throwing
const requestNewsApp = (url, params, headers) => {
  try {
    return Backend.request(url, 'get', { params, headers, auth: true });
  } catch (err) {
    const { status } = err?.response || {};
    if (status >= 400) {
      return err?.response;
    } else {
      const { message } = err.toJSON();
      throw new Error(message);
    }
  }
};

const LD_HEADERS = { accept: 'application/ld+json' };
const JSON_HEADERS = { accept: 'application/json' };

const newsImages = {
  create: (img, news_id, intro = false, session_interval = 0) =>
    createImage(CAT_NEWS, img, { news_id, intro, session_interval }),
  remove: (id) => apiDelete(`${NEWS_PATHS.newsImages}/${id}`),
  getList: (news_id, params = {}) =>
    apiGet(NEWS_PATHS.newsImages, {
      params: {
        ...params,
        'news.id': news_id,
      },
    }),
  update: (id, file, session_interval = 0) => {
    const formData = new FormData();
    formData.append('file', file);
    return Backend.post({
      url: `${NEWS_PATHS.newsImages}/${id}`,
      params: {
        session_interval,
      },
      headers: {
        'Content-type': 'multipart/form-data',
      },
      data: formData,
    });
  },
};

export const newsApiAdapter = {
  newsImages,
  [CAT_NEWS]: {
    getList: (params) => apiGet(NEWS_PATHS.news, { params }),
    getLDList: (params, headers) => {
      const { clearParams, ...rest } = params;
      let preparedParams = {
        params: rest,
        clearParams,
      };
      if (headers) {
        preparedParams.headers = headers;
      }
      return apiGet(NEWS_PATHS.news + NEWS_PATHS.suffix, preparedParams);
    },
    getItem: (id, params) => apiGet(`${NEWS_PATHS.news}/${id}`, { ...params }),
    create: async (data) => {
      const res = await apiPost(NEWS_PATHS.news, { data });
      await uploadImages(CAT_NEWS, data.images, {
        news_id: res.data.id,
        session_interval: data?.sessionInterval || 0,
      }).catch();
      return res;
    },
    update: async (id, { images, sessionInterval, ...data } = {}) => {
      const payload = sessionInterval !== null && sessionInterval !== undefined ? { ...data, sessionInterval } : data;
      const hasNonSessionChanges = Object.keys(data || {}).length > 0;
      const hasImagesToSync = Array.isArray(images) && images.length > 0;

      // If only images changed, skip redundant news PATCH with session-only payload.
      const res = hasNonSessionChanges
        ? await apiPatch(`${NEWS_PATHS.news}/${id}`, {
            data: payload,
            is_protected: true,
          })
        : null;

      if (images?.length) {
        await Promise.all(
          images.map(({ id: imgId, value, isNew, data: imgData }) => {
            if (!value) {
              return Promise.resolve();
            }
            if (imgId) {
              return newsImages.update(imgId, value, sessionInterval || 0);
            } else if (isNew) {
              return newsImages.create(value, id, imgData.intro, sessionInterval || 0);
            }
          })
        );
      }
      if (res) {
        return res;
      }

      if (hasImagesToSync) {
        return { status: 200, data: null };
      }

      return null;
    },
    remove: (id) => apiDelete(`${NEWS_PATHS.news}/${id}`),
  },

  newsDomains: {
    getList: (params) => requestNewsApp(NEWS_PATHS.appDomainsLD, params, LD_HEADERS),
  },
  newsTags: {
    getList: (params) => requestNewsApp(NEWS_PATHS.appTagsLD, params, LD_HEADERS),
  },
  [CAT_COMPANY_NEWS]: {
    getList: (params) => requestNewsApp(NEWS_PATHS.appNewsLD, params, { ...LD_HEADERS, 'WIMS-loggable': true }),
    getItem: (id, params) => requestNewsApp(`${NEWS_PATHS.appNews}/${id}`, params, JSON_HEADERS),
  },
  companyNews: {
    getCompanyNewsList: async (params) => requestNewsApp(NEWS_PATHS.appNews, params, JSON_HEADERS),
  },

  [CAT_AI_NEWS]: {
    getList: (params) => apiGet(NEWS_PATHS.aiNews, { params }),
    update: (id, data) => apiPut(`${NEWS_PATHS.aiNews}/${id}`, { data }),
  },
  aiNewsPrompts: {
    getList: (params) => apiGet(NEWS_PATHS.aiPrompts, { params }),
    getItem: (id) => apiGet(`${NEWS_PATHS.aiPrompts}/${id}`),
    create: (data) => apiPost(NEWS_PATHS.aiPrompts, { data }),
    update: (id, data) => apiPut(`${NEWS_PATHS.aiPrompts}/${id}`, { data }),
    delete: (id) => apiDelete(`${NEWS_PATHS.aiPrompts}/${id}`),
  },
  aiNewsDomains: {
    getList: (params) => apiGet(NEWS_PATHS.aiDomains, { params }),
  },
  aiNewsTags: {
    getList: (params) => apiGet(NEWS_PATHS.aiTags, { params }),
  },
  runSearchAiPrompt: (id) => apiPost(NEWS_PATHS.aiPromptsRun, { params: { id } }),
};
